import React, { useState, useEffect, useMemo } from 'react';
import StatCoachModal from '../../components/analytics/StatCoachModal';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import '../../components/tracker/Tracker.css';
import '../../components/tracker/PlayerDashboard.css';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';

// ─────────────────────────────────────────────
// BENCHMARKS BY PLAYING LEVEL
// ─────────────────────────────────────────────

const BENCHMARK_TIERS = {
  youth_low: {
    label: 'Youth (8U-10U)',
    batting: { avg: { poor: 0.200, solid: 0.300, good: 0.375, elite: 0.450 },
               obp: { poor: 0.280, solid: 0.370, good: 0.440, elite: 0.520 },
               kPct: { elite: 15, good: 25, solid: 35, poor: 50 },
               bbPct: { elite: 18, good: 12, solid: 7, poor: 3 } },
    pitching: { era: { elite: 1.0, good: 2.0, solid: 3.5, poor: 5.0 },
                whip: { elite: 0.8, good: 1.1, solid: 1.5, poor: 2.0 },
                k9: { elite: 10, good: 7, solid: 5, poor: 3 } },
  },
  youth_mid: {
    label: 'Youth (11U-12U)',
    batting: { avg: { poor: 0.220, solid: 0.300, good: 0.375, elite: 0.440 },
               obp: { poor: 0.300, solid: 0.380, good: 0.450, elite: 0.520 },
               kPct: { elite: 15, good: 25, solid: 35, poor: 50 },
               bbPct: { elite: 16, good: 11, solid: 6, poor: 3 } },
    pitching: { era: { elite: 1.2, good: 2.2, solid: 3.8, poor: 5.5 },
                whip: { elite: 0.9, good: 1.2, solid: 1.6, poor: 2.1 },
                k9: { elite: 10, good: 7, solid: 5, poor: 3 } },
  },
  youth_high: {
    label: 'Youth (13U-14U)',
    batting: { avg: { poor: 0.230, solid: 0.290, good: 0.360, elite: 0.420 },
               obp: { poor: 0.300, solid: 0.370, good: 0.440, elite: 0.510 },
               kPct: { elite: 15, good: 25, solid: 35, poor: 50 },
               bbPct: { elite: 15, good: 10, solid: 6, poor: 3 } },
    pitching: { era: { elite: 1.5, good: 2.5, solid: 4.0, poor: 6.0 },
                whip: { elite: 0.9, good: 1.2, solid: 1.6, poor: 2.1 },
                k9: { elite: 11, good: 8, solid: 5, poor: 3 } },
  },
  freshman: {
    label: 'Freshman/JV2',
    batting: { avg: { poor: 0.220, solid: 0.270, good: 0.330, elite: 0.390 },
               obp: { poor: 0.290, solid: 0.350, good: 0.420, elite: 0.490 },
               kPct: { elite: 15, good: 25, solid: 35, poor: 50 },
               bbPct: { elite: 15, good: 10, solid: 6, poor: 3 } },
    pitching: { era: { elite: 1.5, good: 2.5, solid: 4.0, poor: 6.0 },
                whip: { elite: 0.9, good: 1.2, solid: 1.7, poor: 2.2 },
                k9: { elite: 10, good: 7, solid: 5, poor: 3 } },
  },
  jv: {
    label: 'JV',
    batting: { avg: { poor: 0.230, solid: 0.280, good: 0.340, elite: 0.400 },
               obp: { poor: 0.300, solid: 0.360, good: 0.430, elite: 0.500 },
               kPct: { elite: 15, good: 25, solid: 35, poor: 50 },
               bbPct: { elite: 15, good: 10, solid: 6, poor: 3 } },
    pitching: { era: { elite: 2.0, good: 3.0, solid: 4.5, poor: 6.5 },
                whip: { elite: 1.0, good: 1.3, solid: 1.8, poor: 2.3 },
                k9: { elite: 10, good: 7, solid: 5, poor: 3 } },
  },
  varsity: {
    label: 'Varsity',
    batting: { avg: { poor: 0.240, solid: 0.290, good: 0.350, elite: 0.400 },
               obp: { poor: 0.310, solid: 0.370, good: 0.440, elite: 0.500 },
               kPct: { elite: 15, good: 25, solid: 35, poor: 50 },
               bbPct: { elite: 15, good: 10, solid: 6, poor: 3 } },
    pitching: { era: { elite: 2.0, good: 3.5, solid: 5.0, poor: 7.0 },
                whip: { elite: 1.0, good: 1.3, solid: 1.8, poor: 2.3 },
                k9: { elite: 10, good: 7, solid: 5, poor: 3 } },
  },
  college: {
    label: 'College',
    batting: { avg: { poor: 0.250, solid: 0.290, good: 0.330, elite: 0.370 },
               obp: { poor: 0.320, solid: 0.370, good: 0.430, elite: 0.490 },
               kPct: { elite: 15, good: 22, solid: 30, poor: 40 },
               bbPct: { elite: 15, good: 10, solid: 6, poor: 3 } },
    pitching: { era: { elite: 2.5, good: 3.5, solid: 5.0, poor: 7.0 },
                whip: { elite: 1.0, good: 1.3, solid: 1.8, poor: 2.3 },
                k9: { elite: 10, good: 7, solid: 5, poor: 3 } },
  },
};

const LEVEL_TO_TIER = {
  '8U': 'youth_low', '9U': 'youth_low', '10U': 'youth_low',
  '11U': 'youth_mid', '12U': 'youth_mid',
  '6th Grade': 'youth_mid', '7th Grade': 'youth_mid',
  '13U': 'youth_high', '14U': 'youth_high', '8th Grade': 'youth_high',
  'Freshman': 'freshman', 'JV2': 'freshman',
  '15U': 'freshman', '16U': 'freshman',
  'JV': 'jv', '18U': 'jv',
  'Varsity': 'varsity',
  'College': 'college',
};

function getBenchmarkTier(playingLevel) {
  if (!playingLevel) return null;
  const tierKey = LEVEL_TO_TIER[playingLevel];
  return tierKey ? BENCHMARK_TIERS[tierKey] : null;
}

function getBenchmarkRating(value, thresholds, inverse = false) {
  if (value == null) return null;
  if (!inverse) {
    if (value >= thresholds.elite) return { label: 'Elite', color: '#68d391', emoji: '🔥' };
    if (value >= thresholds.good) return { label: 'Above avg', color: '#90cdf4', emoji: '' };
    if (value >= thresholds.solid) return { label: 'Solid', color: '#e8c84a', emoji: '✅' };
    return { label: 'Developing', color: '#fc8181', emoji: '' };
  } else {
    if (value <= thresholds.elite) return { label: 'Elite', color: '#68d391', emoji: '🔥' };
    if (value <= thresholds.good) return { label: 'Above avg', color: '#90cdf4', emoji: '' };
    if (value <= thresholds.solid) return { label: 'Solid', color: '#e8c84a', emoji: '✅' };
    return { label: 'Developing', color: '#fc8181', emoji: '' };
  }
}

function BenchmarkBadge({ rating, levelLabel }) {
  if (!rating || !levelLabel) return null;
  return (
    <div className="bench-badge" style={{ color: rating.color }}>
      {rating.emoji} {rating.label} for {levelLabel}
    </div>
  );
}

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatAvg(val) {
  if (val == null || isNaN(val)) return '—';
  return val.toFixed(3).replace(/^0/, '');
}

function formatEra(val) {
  if (val == null || isNaN(val) || !isFinite(val)) return '—';
  return val.toFixed(2);
}

// ─────────────────────────────────────────────
// STAT AGGREGATION
// ─────────────────────────────────────────────

function aggregateBatting(games) {
  const totals = {
    pa: 0,
    ab: 0,
    h: 0,
    r: 0,
    rbi: 0,
    bb: 0,
    ibb: 0,
    k: 0,
    hr: 0,
    '1b': 0,
    '2b': 0,
    '3b': 0,
    sb: 0,
    cs: 0,
    hbp: 0,
    sf: 0,
    sh: 0,
    roe: 0,
    fc: 0,
    gidp: 0,
    ab_risp: 0,
    h_risp: 0,
    rbi_risp: 0,
  };
  let gameCount = 0;
  let twoOutAB = 0, twoOutH = 0, twoOutRBI = 0, twoOutRISP_AB = 0, twoOutRISP_H = 0;
  for (const g of games) {
    const b = g.batting;
    if (!b) continue;
    gameCount++;
    for (const key of Object.keys(totals)) {
      totals[key] += Number(b[key] || 0);
    }
    // 2-out situational stats from plate_appearances
    if (Array.isArray(b.plate_appearances)) {
      for (const pa of b.plate_appearances) {
        if (pa.outCount !== 2) continue;
        const isHit = ['single', '1b', 'double', '2b', 'triple', '3b', 'hr', 'iphr'].includes(pa.outcome);
        const isAB = !['bb', 'ibb', 'hbp', 'sf', 'sh'].includes(pa.outcome);
        if (isAB) {
          twoOutAB++;
          if (isHit) twoOutH++;
          if (pa.rbi) twoOutRBI += Number(pa.rbi);
          if (pa.risp) {
            twoOutRISP_AB++;
            if (isHit) twoOutRISP_H++;
          }
        }
      }
    }
  }
  if (gameCount === 0) return null;

  const avg = totals.ab > 0 ? totals.h / totals.ab : null;
  const obpNum = totals.h + totals.bb + totals.hbp;
  const obpDen = totals.ab + totals.bb + totals.hbp + totals.sf;
  const obp = obpDen > 0 ? obpNum / obpDen : null;
  const tb = totals['1b'] + 2 * totals['2b'] + 3 * totals['3b'] + 4 * totals.hr;
  const slg = totals.ab > 0 ? tb / totals.ab : null;
  const ops = obp != null && slg != null ? obp + slg : null;
  const kPct = totals.pa > 0 ? (totals.k / totals.pa) * 100 : null;
  const bbPct = totals.pa > 0 ? (totals.bb / totals.pa) * 100 : null;
  const babipDen = totals.ab - totals.k - totals.hr;
  const babip = babipDen > 0 ? (totals.h - totals.hr) / babipDen : null;
  const contactPct = totals.ab > 0 ? ((totals.ab - totals.k) / totals.ab) * 100 : null;
  const twoOutAvg = twoOutAB > 0 ? twoOutH / twoOutAB : null;
  const twoOutRISPAvg = twoOutRISP_AB > 0 ? twoOutRISP_H / twoOutRISP_AB : null;
  return { ...totals, avg, obp, slg, ops, tb, kPct, bbPct, babip, contactPct, gameCount, twoOutAB, twoOutH, twoOutRBI, twoOutAvg, twoOutRISP_AB, twoOutRISP_H, twoOutRISPAvg };
}

function aggregatePitching(games) {
  let ipOuts = 0;
  let k = 0,
    bb = 0,
    h = 0,
    r = 0,
    er = 0,
    hr = 0,
    hbp = 0,
    wp = 0,
    pitches = 0;
  let fps = 0,
    fpb = 0;
  let totalStrikes = 0,
    totalPitchesForPct = 0;
  let pitchTypeAgg = {};
  let velocities = [];
  let gameCount = 0;
  let wins = 0, losses = 0, saves = 0, holds = 0, blownSaves = 0;
  let sbAllowed = 0, csAttempts = 0, csOuts = 0;
  let balks = 0, pickoffs = 0;

  for (const g of games) {
    const p = g.pitching;
    if (!p) continue;
    gameCount++;
    if (p.decision === 'W') wins++;
    else if (p.decision === 'L') losses++;
    else if (p.decision === 'S') saves++;
    else if (p.decision === 'H') holds++;
    else if (p.decision === 'BS') blownSaves++;
    sbAllowed += Number(p.sb_allowed || 0);
    csAttempts += Number(p.cs_attempts || 0);
    csOuts += Number(p.cs_outs || 0);
    balks += Number(p.balks || 0);
    pickoffs += Number(p.pickoffs || 0);
    const ipVal = parseFloat(p.ip || 0);
    const full = Math.floor(ipVal);
    const outs = Math.round((ipVal - full) * 10);
    ipOuts += full * 3 + outs;
    k += Number(p.k || 0);
    bb += Number(p.bb || 0);
    h += Number(p.h || 0);
    r += Number(p.r || 0);
    er += Number(p.er || 0);
    hr += Number(p.hr || 0);
    hbp += Number(p.hbp || 0);
    wp += Number(p.wp || 0);
    pitches += Number(p.pitches || 0);
    fps += Number(p.first_pitch_strikes || 0);
    fpb += Number(p.first_pitch_balls || 0);
    if (p.avg_velo) velocities.push(Number(p.avg_velo));
    // Use saved totals if available, otherwise calculate from detailed_pitches
    if (p.total_strikes != null && p.total_pitches != null) {
      totalStrikes += Number(p.total_strikes);
      totalPitchesForPct += Number(p.total_pitches);
    } else if (p.detailed_pitches && Array.isArray(p.detailed_pitches)) {
      for (const pitch of p.detailed_pitches) {
        totalPitchesForPct++;
        if (pitch.outcome === 'strike' || pitch.outcome === 'ballInPlay') {
          totalStrikes++;
        }
      }
    }
    if (p.pitch_type_stats) {
      for (const [type, stats] of Object.entries(p.pitch_type_stats)) {
        if (!pitchTypeAgg[type]) {
          pitchTypeAgg[type] = {
            total: 0, strikes: 0, balls: 0,
            swinging: 0, looking: 0, foul: 0, foulTip: 0, ballInPlay: 0,
          };
        }
        const agg = pitchTypeAgg[type];
        agg.total += Number(stats.total || 0);
        agg.strikes += Number(stats.strikes || 0);
        agg.balls += Number(stats.balls || 0);
        agg.swinging += Number(stats.swinging || 0);
        agg.looking += Number(stats.looking || 0);
        agg.foul += Number(stats.foul || 0);
        agg.foulTip += Number(stats.foulTip || 0);
        agg.ballInPlay += Number(stats.ballInPlay || 0);
      }
    } else if (p.detailed_pitches && Array.isArray(p.detailed_pitches)) {
      for (const pitch of p.detailed_pitches) {
        const type = pitch.type || 'Unknown';
        if (type === 'Unknown') continue;
        if (!pitchTypeAgg[type]) {
          pitchTypeAgg[type] = {
            total: 0, strikes: 0, balls: 0,
            swinging: 0, looking: 0, foul: 0, foulTip: 0, ballInPlay: 0,
          };
        }
        const agg = pitchTypeAgg[type];
        agg.total++;
        if (pitch.outcome === 'strike' || pitch.outcome === 'ballInPlay') {
          agg.strikes++;
          if (pitch.detail === 'swinging') agg.swinging++;
          if (pitch.detail === 'looking') agg.looking++;
          if (pitch.detail === 'foul') agg.foul++;
          if (pitch.detail === 'foulTip') agg.foulTip++;
          if (pitch.outcome === 'ballInPlay') agg.ballInPlay++;
        } else {
          agg.balls++;
        }
      }
    }
  }
  if (gameCount === 0) return null;

  const ipFull = Math.floor(ipOuts / 3);
  const ipRem = ipOuts % 3;
  const ipDisplay = `${ipFull}.${ipRem}`;
  const ipDecimal = ipFull + ipRem / 3;

  const era = ipDecimal > 0 ? (er / ipDecimal) * 9 : null;
  const whip = ipDecimal > 0 ? (bb + h) / ipDecimal : null;
  const k9 = ipDecimal > 0 ? (k / ipDecimal) * 9 : null;
  const bb9 = ipDecimal > 0 ? (bb / ipDecimal) * 9 : null;
  const kBb = bb > 0 ? k / bb : null;
  const fpsPct = fps + fpb > 0 ? (fps / (fps + fpb)) * 100 : null;
  const avgVelo =
    velocities.length > 0
      ? velocities.reduce((a, b) => a + b, 0) / velocities.length
      : null;
  const fip = ipDecimal > 0
    ? ((13 * hr) + (3 * (bb + hbp)) - (2 * k)) / ipDecimal + 3.10
    : null;

  const strikePct = totalPitchesForPct > 0
    ? Math.round((totalStrikes / totalPitchesForPct) * 100)
    : null;
  // Calculate per-type percentages
  const pitchTypeStats = Object.entries(pitchTypeAgg).map(([type, s]) => ({
    type,
    total: s.total,
    strikes: s.strikes,
    balls: s.balls,
    swinging: s.swinging,
    looking: s.looking,
    foul: s.foul,
    foulTip: s.foulTip,
    ballInPlay: s.ballInPlay,
    strikePct: s.total > 0 ? Math.round((s.strikes / s.total) * 100) : 0,
    swingingPct: s.total > 0 ? Math.round((s.swinging / s.total) * 100) : 0,
    lookingPct: s.total > 0 ? Math.round((s.looking / s.total) * 100) : 0,
    foulPct: s.total > 0 ? Math.round((s.foul / s.total) * 100) : 0,
    ballInPlayPct: s.total > 0 ? Math.round((s.ballInPlay / s.total) * 100) : 0,
    usagePct: totalPitchesForPct > 0 ? Math.round((s.total / totalPitchesForPct) * 100) : 0,
  })).sort((a, b) => b.total - a.total);

  return {
    ip: ipDisplay,
    ipDecimal,
    wins,
    losses,
    saves,
    holds,
    blownSaves,
    k,
    bb,
    h,
    r,
    er,
    hr,
    hbp,
    wp,
    pitches,
    era,
    whip,
    fip,
    k9,
    bb9,
    kBb,
    fpsPct,
    strikePct,
    totalStrikes,
    pitchTypeStats,
    avgVelo,
    gameCount,
    sbAllowed,
    csAttempts,
    csOuts,
    balks,
    pickoffs,
    bf: k + bb + hbp + h + Math.floor(ipDecimal) * 3 + (ipOuts % 3),
  };
}

function aggregateFielding(games) {
  const byPos = {};
  for (const g of games) {
    if (!g.fielding || !Array.isArray(g.fielding)) continue;
    for (const pos of g.fielding) {
      if (!byPos[pos.position]) {
        byPos[pos.position] = {
          position: pos.position,
          innings: 0,
          po: 0,
          assists: 0,
          errors: 0,
          dp: 0,
          tp: 0,
          passedBalls: 0,
          scoredOnPassedBall: 0,
          sbAllowed: 0,
          caughtStealing: 0,
          blockedBalls: 0,
          droppedThird: 0,
          pickoffCS: 0,
          scoops: 0,
        };
      }
      const e = byPos[pos.position];
      e.innings += Number(pos.innings || 0);
      e.po += Number(pos.po || 0);
      e.assists += Number(pos.a || pos.assists || 0);
      e.errors += Number(pos.e || pos.errors || 0);
      e.dp += Number(pos.dp || 0);
      e.tp += Number(pos.tp || 0);
      e.passedBalls += Number(pos.passedBalls || pos.pb || 0);
      e.scoredOnPassedBall += Number(pos.scoredOnPassedBall || pos.scored_on_passed_ball || 0);
      e.sbAllowed += Number(pos.sbAllowed || 0);
      e.caughtStealing += Number(pos.caughtStealing || 0);
      e.blockedBalls += Number(pos.blockedBalls || 0);
      e.droppedThird += Number(pos.droppedThird || 0);
      e.pickoffCS += Number(pos.pickoffCS || 0);
      e.scoops += Number(pos.scoops || 0);
    }
  }
  const positions = Object.values(byPos);
  if (positions.length === 0) return null;

  for (const p of positions) {
    const tc = p.po + p.assists + p.errors;
    p.tc = tc;
    p.fpct = tc > 0 ? (p.po + p.assists) / tc : 1.0;
  }

  const totals = positions.reduce(
    (acc, p) => ({
      po: acc.po + p.po,
      assists: acc.assists + p.assists,
      errors: acc.errors + p.errors,
      dp: acc.dp + p.dp,
      tc: acc.tc + p.tc,
    }),
    { po: 0, assists: 0, errors: 0, dp: 0, tc: 0 }
  );
  totals.fpct = totals.tc > 0 ? (totals.po + totals.assists) / totals.tc : 1.0;

  return { positions, totals };
}

// ─────────────────────────────────────────────
// TREND CALCULATION
// ─────────────────────────────────────────────

function calcTrend(games, statFn) {
  if (games.length < 4) return null;
  const sorted = [...games].sort(
    (a, b) => new Date(b.gameDate) - new Date(a.gameDate)
  );
  const half = Math.max(2, Math.floor(games.length / 2));
  const recentGames = sorted.slice(0, half);
  const prevGames = sorted.slice(half, half * 2);
  if (prevGames.length === 0) return null;
  const recent = statFn(recentGames);
  const previous = statFn(prevGames);
  if (recent == null || previous == null) return null;
  const delta = recent - previous;
  const threshold = Math.abs(previous) * 0.04;
  return {
    direction: delta > threshold ? 'up' : delta < -threshold ? 'down' : 'flat',
    recent,
    previous,
    delta,
  };
}

// ─────────────────────────────────────────────
// SMART INSIGHTS
// ─────────────────────────────────────────────

function generateInsights(games, battingStats, pitchingStats) {
  const insights = [];
  if (games.length < 3) return insights;
  const sorted = [...games].sort(
    (a, b) => new Date(b.gameDate) - new Date(a.gameDate)
  );

  // ── Batting ───────────────────────────────
  if (battingStats && battingStats.gameCount >= 3) {
    // K% recent trend
    const recentBat = sorted.slice(0, 3).filter((g) => g.batting);
    const recentPA = recentBat.reduce((a, g) => a + (g.batting.pa || 0), 0);
    const recentK = recentBat.reduce((a, g) => a + (g.batting.k || 0), 0);
    const recentKPct = recentPA > 0 ? (recentK / recentPA) * 100 : null;

    if (recentKPct != null && recentKPct > 30) {
      insights.push({
        type: 'warning',
        icon: '⚠️',
        title: 'Strikeout Rate Climbing',
        body: `K/PA in last 3 games is ${recentKPct.toFixed(0)}%. Focus on contact approach and shortening the swing with 2 strikes.`,
        drill: 'Two-strike tee work: set tee at middle-away, focus on driving the ball the other way. Practice "choke up and put it in play" approach with 2 strikes. Goal: make contact, not damage.',
      });
    } else if (
      recentKPct != null &&
      recentKPct < 15 &&
      battingStats.gameCount >= 5
    ) {
      insights.push({
        type: 'positive',
        icon: '🎯',
        title: 'Strong Contact Rate',
        body: `K/PA of ${recentKPct.toFixed(0)}% in recent games shows excellent bat-to-ball skills.`,
        drill: 'Maintain current approach. Add opposite field tee work to stay balanced. Challenge yourself with breaking ball recognition in BP.',
      });
    }

    // RISP performance
    if (battingStats.ab_risp >= 5) {
      const rispAvg = battingStats.h_risp / battingStats.ab_risp;
      const diff = rispAvg - (battingStats.avg || 0);
      if (diff > 0.05) {
        insights.push({
          type: 'positive',
          icon: '🔥',
          title: 'Clutch Performer',
          body: `Hitting ${formatAvg(rispAvg)} with RISP vs ${formatAvg(battingStats.avg)} overall — steps up when it counts.`,
          drill: 'Keep doing what you\'re doing. Add pressure situation BP — runners on 2nd and 3rd, coach calling out scenarios before each pitch.',
        });
      } else if (diff < -0.05 && battingStats.ab_risp >= 8) {
        insights.push({
          type: 'warning',
          icon: '💡',
          title: 'RISP Opportunity',
          body: `Hitting ${formatAvg(rispAvg)} with RISP vs ${formatAvg(battingStats.avg)} overall. Work on situational hitting and managing pressure at-bats.`,
          drill: 'Situational hitting BP: runners on base scenarios, focus on driving the ball to the right side. Practice "see ball, hit ball" — simplify approach with runners on.',
        });
      }
    }

    // Home vs Away split
    const homeGames = games.filter((g) => g.homeAway === 'home' && g.batting);
    const awayGames = games.filter((g) => g.homeAway === 'away' && g.batting);
    if (homeGames.length >= 3 && awayGames.length >= 3) {
      const hs = aggregateBatting(homeGames);
      const as_ = aggregateBatting(awayGames);
      if (hs && as_ && hs.avg != null && as_.avg != null) {
        const diff = hs.avg - as_.avg;
        if (Math.abs(diff) > 0.05) {
          insights.push({
            type: diff > 0 ? 'info' : 'warning',
            icon: diff > 0 ? '🏠' : '✈️',
            title: diff > 0 ? 'Strong Home Performance' : 'Road Trip Challenge',
            body: `Hitting ${formatAvg(hs.avg)} at home vs ${formatAvg(as_.avg)} away — a ${Math.abs(Math.round(diff * 1000))}-point split worth monitoring.`,
          });
        }
      }
    }

    // Plate discipline
    if (battingStats.bbPct != null && battingStats.bbPct > 12) {
      insights.push({
        type: 'positive',
        icon: '👁',
        title: 'Excellent Plate Discipline',
        body: `BB% of ${battingStats.bbPct.toFixed(1)}% shows elite strike zone awareness. Keep working counts and making pitchers work.`,
        drill: 'Continue working counts. Add pitch recognition drills — have coach call ball/strike before you swing to sharpen zone awareness.',
      });
    }
  }

  insights.forEach((i) => { if (!i.category) i.category = 'batting'; });

  // ── Hot / Cold streak ─────────────────────
  if (battingStats && battingStats.gameCount >= 3) {
    // Hot streak detection (3+ consecutive games with a hit)
    const recentWithHits = sorted
      .slice(0, 5)
      .filter((g) => g.batting && g.batting.h > 0);
    if (recentWithHits.length >= 3) {
      insights.push({
        type: 'positive',
        icon: '🔥',
        title: 'On a Hot Streak',
        body: `Hits in ${recentWithHits.length} of the last 5 games — staying consistent at the plate.`,
        drill: 'Keep your routine locked in. Visualize success before each at-bat. Hot streaks reward consistent mechanics — don\'t change what\'s working.',
      });
    }

    // Cold streak detection (0 hits in last 3 games with 3+ AB each)
    const recentCold = sorted
      .slice(0, 3)
      .filter((g) => g.batting && g.batting.ab >= 3 && g.batting.h === 0);
    if (recentCold.length === 3) {
      insights.push({
        type: 'warning',
        icon: '❄️',
        title: 'Cold Stretch — Reset Time',
        body: `0 hits in last 3 games (${recentCold.reduce((a, g) => a + g.batting.ab, 0)} AB). Time to simplify and reset approach.`,
        drill: 'Soft toss reset: 50 reps off soft toss focusing only on making solid contact middle of the field. No power — just line drives. Rebuild confidence with contact before adding aggression.',
      });
    }

    // Power emerging detection
    if (battingStats.hr >= 2 && battingStats.gameCount <= 8) {
      insights.push({
        type: 'positive',
        icon: '💥',
        title: 'Power Emerging',
        body: `${battingStats.hr} HR in ${battingStats.gameCount} games — above-average power production for this sample.`,
        drill: 'Maintain current hip rotation and extension through the ball. Add heavy bag or resistance band hip turn drills to keep explosiveness sharp.',
      });
    }

    // Walk rate surge
    const recentBB = sorted.slice(0, 3).filter((g) => g.batting);
    const recentBBTotal = recentBB.reduce((a, g) => a + (g.batting.bb || 0), 0);
    const recentPATotal = recentBB.reduce((a, g) => a + (g.batting.pa || 0), 0);
    if (recentPATotal >= 6 && recentBBTotal / recentPATotal > 0.2) {
      insights.push({
        type: 'positive',
        icon: '👁',
        title: 'Elite Patience — Recent Games',
        body: `Walk rate above 20% in last 3 games. Pitchers can't find the zone against you right now.`,
        drill: 'Trust your eye — keep taking pitches early in counts. Look for your pitch to drive, not just walk. Controlled aggression on strikes.',
      });
    }
  }

  insights.forEach((i) => { if (!i.category) i.category = 'batting'; });

  // ── Pitching velocity + efficiency ────────
  if (pitchingStats && pitchingStats.gameCount >= 3) {
    // Velocity trend
    const gamesWithVelo = sorted
      .filter((g) => g.pitching && g.pitching.avg_velo > 0)
      .slice(0, 4);
    if (gamesWithVelo.length >= 3) {
      const veloValues = gamesWithVelo.map((g) => g.pitching.avg_velo);
      const recentVelo = veloValues.slice(0, 2).reduce((a, b) => a + b, 0) / 2;
      const earlierVelo =
        veloValues.slice(2).reduce((a, b) => a + b, 0) / veloValues.slice(2).length;
      const veloDrop = earlierVelo - recentVelo;
      if (veloDrop >= 2) {
        insights.push({
          type: 'warning',
          icon: '⚡',
          title: 'Velocity Dip Detected',
          body: `Average velocity down ~${Math.round(veloDrop)} mph compared to earlier outings. Could indicate fatigue or mechanical drift.`,
          drill: 'Rest arm 48-72 hours minimum. When returning: long toss program starting at 60ft, working to comfortable max distance. Focus on hip-to-shoulder separation — velocity lives in the hips, not the arm.',
        });
      }
    }

    // Pitch count efficiency
    if (pitchingStats.pitches > 0 && pitchingStats.ip) {
      const ipVal = parseFloat(pitchingStats.ip);
      const full = Math.floor(ipVal);
      const outs = Math.round((ipVal - full) * 10);
      const totalOuts = full * 3 + outs;
      const pitchesPerOut = totalOuts > 0 ? pitchingStats.pitches / totalOuts : null;
      if (pitchesPerOut && pitchesPerOut > 6) {
        insights.push({
          type: 'warning',
          icon: '📊',
          title: 'High Pitch Count Per Out',
          body: `Averaging ${pitchesPerOut.toFixed(1)} pitches per out — working deep into counts frequently. This limits innings pitched.`,
          drill: 'Challenge drill: in bullpen, pretend every batter starts 0-2. Throw first pitch as if you\'re finishing them off. Goal: train the mindset of attacking hitters early.',
        });
      }
    }
  }

  insights.forEach((i) => { if (!i.category) i.category = 'pitching'; });

  // ── Pitching ──────────────────────────────
  if (pitchingStats && pitchingStats.gameCount >= 3) {
    if (pitchingStats.era != null) {
      if (pitchingStats.era < 2.0) {
        insights.push({
          type: 'positive',
          icon: '🔥',
          title: 'Elite ERA',
          body: `ERA of ${formatEra(pitchingStats.era)} is exceptional. Maintain current approach and pitch sequencing.`,
          drill: 'Maintain current pitch sequencing. Chart your go-to pitch by count and keep trusting it. Review video of your best outings for mechanical consistency.',
        });
      } else if (pitchingStats.era > 5.0) {
        insights.push({
          type: 'warning',
          icon: '⚠️',
          title: 'ERA Needs Attention',
          body: `ERA of ${formatEra(pitchingStats.era)} suggests command or stuff issues. Review pitch data for patterns by count.`,
          drill: 'Bullpen session focused on first-pitch strikes. Throw 30 pitches — goal is 65%+ strikes. Review pitch data by count to find where you\'re losing the zone.',
        });
      }
    }
    if (pitchingStats.kBb != null) {
      if (pitchingStats.kBb >= 3.0) {
        insights.push({
          type: 'positive',
          icon: '🎯',
          title: 'Dominant K/BB Ratio',
          body: `K/BB of ${pitchingStats.kBb.toFixed(2)} — striking out batters without giving free passes. Elite command.`,
          drill: 'Stay aggressive in the zone. Add secondary pitch command work — throw your off-speed to both sides of the plate in flat-ground sessions.',
        });
      } else if (pitchingStats.kBb < 1.5) {
        insights.push({
          type: 'warning',
          icon: '💡',
          title: 'Command Focus Area',
          body: `K/BB of ${pitchingStats.kBb.toFixed(2)} — work on getting ahead early in counts to improve this ratio.`,
          drill: 'Target practice: tape a 12-inch square on a net, throw 20 pitches per session aiming for the corners. Get ahead early — fastball first-pitch strike is priority one.',
        });
      }
    }
    if (pitchingStats.fpsPct != null && pitchingStats.fpsPct < 50) {
      insights.push({
        type: 'warning',
        icon: '⚠️',
        title: 'First Pitch Strikes',
        body: `Only ${pitchingStats.fpsPct.toFixed(0)}% first pitch strikes. Getting ahead early dramatically changes outcomes — prioritize strike one.`,
        drill: 'Bullpen drill: throw ONLY first pitches for 15 minutes. Every pitch is a 0-0 count. Goal is 65%+ strikes. Your go-to fastball location — commit to it.',
      });
    }
  }

  insights.forEach((i) => { if (!i.category) i.category = 'pitching'; });

  // ── Guest vs Full Time ────────────────────
  const guestGames = games.filter((g) => g.membership === 'guest' && g.batting);
  const ftGames = games.filter(
    (g) => g.membership === 'full_time' && g.batting
  );
  if (guestGames.length >= 3 && ftGames.length >= 3) {
    const gs = aggregateBatting(guestGames);
    const fs = aggregateBatting(ftGames);
    if (gs && fs && gs.avg != null && fs.avg != null) {
      const diff = gs.avg - fs.avg;
      if (diff > 0.04) {
        insights.push({
          type: 'info',
          icon: '🧠',
          title: 'Pressure Point Detected',
          body: `Hitting ${formatAvg(gs.avg)} as a guest vs ${formatAvg(fs.avg)} on full-time teams. Performing better without the weight of expectation — mental approach work could help replicate this freedom on your primary team.`,
        });
      } else if (diff < -0.04) {
        insights.push({
          type: 'positive',
          icon: '🏆',
          title: 'Thrives Under Pressure',
          body: `Hitting ${formatAvg(fs.avg)} on full-time teams vs ${formatAvg(gs.avg)} as a guest — performs better when it matters most.`,
        });
      }
    }
  }

  insights.forEach((i) => { if (!i.category) i.category = 'batting'; });

  return resolveContradictions(insights);
}

// ─────────────────────────────────────────────
// CONTRADICTION RESOLUTION
// ─────────────────────────────────────────────

function resolveContradictions(insights) {
  const findAndRemove = (title) => {
    const idx = insights.findIndex((i) => i.title === title);
    if (idx === -1) return null;
    return insights.splice(idx, 1)[0];
  };

  // Strikeout Rate Climbing + Excellent Plate Discipline
  const kClimbing = findAndRemove('Strikeout Rate Climbing');
  const platediscipline = findAndRemove('Excellent Plate Discipline');
  if (kClimbing && platediscipline) {
    insights.push({
      type: 'info',
      icon: '🧠',
      title: 'Patient But Not Finishing At-Bats',
      category: 'batting',
      body: `${platediscipline.body.split('.')[0]}, but the strikeout rate is climbing at the same time. This usually means good counts are being worked early, then at-bats aren't getting finished — especially with two strikes. Worth digging into: is there a specific pitch type or location causing trouble once the count gets deep?`,
      drill: 'Two-strike approach work: once ahead in the count, shift to a contact-first mindset. Tee work on the outer half with 2 strikes, focusing on putting the ball in play rather than driving it. Track which pitch type/location leads to the most 2-strike whiffs to find the pattern.',
    });
  } else {
    if (kClimbing) insights.push(kClimbing);
    if (platediscipline) insights.push(platediscipline);
  }

  return insights;
}

// ─────────────────────────────────────────────
// HERO BANNER
// ─────────────────────────────────────────────

function getHeroBanner(player, battingStats, pitchingStats,
  battingBenchmarks, pitchingBenchmarks) {
  const name = player?.firstName || 'Your player';
  const level = player?.playingLevel || null;
  const levelLabel = level ? `for ${level}` : '';

  // Pitching elite ERA
  if (pitchingBenchmarks?.era?.label === 'Elite' &&
    pitchingStats?.era != null) {
    return {
      headline: `${name}'s ERA is Elite ${levelLabel} 🔥`,
      sub: `${formatEra(pitchingStats.era)} ERA across ${pitchingStats.gameCount} outings`,
    };
  }
  // Batting elite AVG
  if (battingBenchmarks?.avg?.label === 'Elite' &&
    battingStats?.avg != null) {
    return {
      headline: `${name} is hitting Elite ${levelLabel} 🔥`,
      sub: `${formatAvg(battingStats.avg)} AVG · ${battingStats.h} hits this season`,
    };
  }
  // Pitching above avg ERA
  if (pitchingBenchmarks?.era?.label === 'Above avg' &&
    pitchingStats?.era != null) {
    return {
      headline: `${name} is pitching above average ${levelLabel} 📈`,
      sub: `${formatEra(pitchingStats.era)} ERA · ${pitchingStats.k} strikeouts this season`,
    };
  }
  // Batting above avg
  if (battingBenchmarks?.avg?.label === 'Above avg' &&
    battingStats?.avg != null) {
    return {
      headline: `${name} is hitting above average ${levelLabel} 📈`,
      sub: `${formatAvg(battingStats.avg)} AVG · ${battingStats.h} hits this season`,
    };
  }
  // Hot streak
  if (battingStats?.gameCount >= 3) {
    return {
      headline: `${name}'s season is underway`,
      sub: `${battingStats.gameCount} games tracked this season`,
    };
  }
  // Pitcher default
  if (pitchingStats?.gameCount >= 1) {
    return {
      headline: `${name} is on the mound`,
      sub: `${pitchingStats.gameCount} outings tracked this season`,
    };
  }
  return null;
}

// ─────────────────────────────────────────────
// MILESTONE DETECTION
// ─────────────────────────────────────────────

function detectMilestones(games, battingStats, pitchingStats, player) {
  if (!games || games.length === 0) return [];
  const sorted = [...games].sort(
    (a, b) => new Date(b.gameDate) - new Date(a.gameDate)
  );
  const recent = sorted[0];
  const milestones = [];

  // First career HR (only fires when HR in most recent game pushed total to 1)
  if (battingStats && battingStats.hr === 1 && recent?.batting?.hr > 0) {
    const b = recent.batting;
    const parts = [`${b.h}/${b.ab}`, '1 HR'];
    if (b.rbi > 0) parts.push(`${b.rbi} RBI`);
    milestones.push({ type: 'hr', emoji: '🔥', label: 'First Career HR!', statLine: parts.join(' · '), gameId: recent.id });
  }

  // 3+ hit game (most recent)
  if (recent?.batting?.h >= 3) {
    const b = recent.batting;
    const parts = [`${b.h}/${b.ab}`];
    if (b.hr > 0) parts.push(`${b.hr} HR`);
    if (b.rbi > 0) parts.push(`${b.rbi} RBI`);
    if (b.bb > 0) parts.push(`${b.bb} BB`);
    if (b.sb > 0) parts.push(`${b.sb} SB`);
    milestones.push({ type: 'multihit', emoji: '🔥', label: `${b.h}-Hit Game!`, statLine: parts.join(' · '), gameId: recent.id });
  }

  // 5+ strikeout game for pitchers (most recent)
  if (player?.isPitcher && recent?.pitching?.k >= 5) {
    const p = recent.pitching;
    const parts = [];
    if (p.ip) parts.push(`${p.ip} IP`);
    parts.push(`${p.k} K`);
    if (p.er != null) parts.push(`${p.er} ER`);
    milestones.push({ type: 'strikeouts', emoji: '🔥', label: `${p.k}-Strikeout Game!`, statLine: parts.join(' · '), gameId: recent.id });
  }

  // Season batting avg over .400 (min 10 AB)
  if (battingStats?.avg >= 0.4 && battingStats?.ab >= 10) {
    milestones.push({
      type: 'hot_avg',
      emoji: '🔥',
      label: '.400+ Season AVG!',
      statLine: `${formatAvg(battingStats.avg)} AVG · ${battingStats.h} H, ${battingStats.ab} AB, ${battingStats.rbi} RBI`,
      gameId: recent.id,
    });
  }

  return milestones;
}

// ─────────────────────────────────────────────
// TREND CHART
// ─────────────────────────────────────────────

function TrendChart({ games, statFn, label, color = 'var(--amber)',
  format, inverse = false }) {
  const data = [...games]
    .sort((a, b) => new Date(a.gameDate) - new Date(b.gameDate))
    .map((g) => {
      const val = statFn(g);
      return {
        date: new Date(g.gameDate + 'T12:00:00').toLocaleDateString(
          'en-US', { month: 'short', day: 'numeric' }
        ),
        value: val != null ? Math.round(val * 1000) / 1000 : null,
      };
    })
    .filter((d) => d.value != null);

  if (data.length < 2) return null;

  const values = data.map((d) => d.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const avg = values.reduce((a, b) => a + b, 0) / values.length;
  const padding = (max - min) * 0.2 || 0.5;

  return (
    <div className="dash-trend-chart">
      <div className="dash-trend-chart-label">{label} — per game</div>
      <ResponsiveContainer width="100%" height={140}>
        <LineChart data={data}
          margin={{ top: 8, right: 16, left: -24, bottom: 0 }}>
          <XAxis
            dataKey="date"
            tick={{ fill: 'var(--text-muted, #718096)', fontSize: 10 }}
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            domain={[
              Math.max(0, min - padding),
              max + padding,
            ]}
            tick={{ fill: 'var(--text-muted, #718096)', fontSize: 10 }}
            tickLine={false}
            axisLine={false}
            tickFormatter={format || ((v) => v)}
          />
          <Tooltip
            contentStyle={{
              background: 'var(--surface, #1a202c)',
              border: '1px solid var(--border-light, #2d3748)',
              borderRadius: '8px',
              color: 'var(--text, #fff)',
              fontSize: '12px',
            }}
            formatter={(v) => [format ? format(v) : v, label]}
            labelStyle={{ color: 'var(--text-muted, #718096)' }}
          />
          <ReferenceLine
            y={avg}
            stroke="rgba(255,255,255,0.15)"
            strokeDasharray="4 4"
          />
          <Line
            type="monotone"
            dataKey="value"
            stroke={color}
            strokeWidth={2}
            dot={{ fill: color, r: 3 }}
            activeDot={{ r: 5 }}
            connectNulls
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─────────────────────────────────────────────
// TREND ARROW
// ─────────────────────────────────────────────

function TrendArrow({ direction, inverse = false }) {
  if (!direction || direction === 'flat')
    return <span className="trend-flat"> →</span>;
  const isGood = inverse ? direction === 'down' : direction === 'up';
  return (
    <span className={`trend-arrow ${isGood ? 'good' : 'bad'}`}>
      {direction === 'up' ? ' ↑' : ' ↓'}
    </span>
  );
}

// ─────────────────────────────────────────────
// STAT CELL
// ─────────────────────────────────────────────

function StatCell({ label, value, rating }) {
  return (
    <div className="dash-stat-cell">
      <div className="dash-stat-value" style={rating ? { color: rating.color } : undefined}>
        {value ?? '—'}
      </div>
      <div className="dash-stat-label">{label}</div>
      {rating && <div className="dash-stat-rating-dot" style={{ background: rating.color }} />}
    </div>
  );
}

// ─────────────────────────────────────────────
// INSIGHT CARD
// ─────────────────────────────────────────────

function InsightCard({ insight }) {
  const [showDrill, setShowDrill] = useState(false);
  return (
    <div className={`dash-insight-card ${insight.type}`}>
      <div className="dash-insight-icon">{insight.icon}</div>
      <div className="dash-insight-body">
        <div className="dash-insight-title">{insight.title}</div>
        <div className="dash-insight-text">{insight.body}</div>
        {insight.drill && (
          <div className="dash-insight-drill-wrap">
            <button
              className="dash-insight-drill-toggle"
              onClick={() => setShowDrill(!showDrill)}
            >
              {showDrill ? '▲ Hide drill' : 'Recommended drill'}
            </button>
            {showDrill && (
              <div className="dash-insight-drill">
                {insight.drill}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// MAIN DASHBOARD
// ─────────────────────────────────────────────

export default function PlayerDashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isProOrAbove = ['pro', 'premium', 'organization'].includes(user?.subscriptionPlan);
  const { id } = useParams();

  const [player, setPlayer] = useState(null);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [teamFilter, setTeamFilter] = useState('all');
  const [showInsights, setShowInsights] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [showTrendsBatting, setShowTrendsBatting] = useState(false);
  const [showTrendsPitching, setShowTrendsPitching] = useState(false);
  const [coachModal, setCoachModal] = useState({ open: false, statGroup: null, initialMessage: null });
  const [showGlossary, setShowGlossary] = useState(false);
  const [resumeModalGame, setResumeModalGame] = useState(null);
  const [dismissedKeys, setDismissedKeys] = useState(() => {
    try {
      const stored = localStorage.getItem(`playmaker_milestones_${id}`);
      return new Set(stored ? JSON.parse(stored) : []);
    } catch {
      return new Set();
    }
  });

  useEffect(() => {
    async function fetchData() {
      try {
        const [playerRes, gamesRes] = await Promise.all([
          api.get(`/analytics/players/${id}`),
          api.get(`/analytics/players/${id}/games`),
        ]);
        setPlayer(playerRes.data);
        setGames(gamesRes.data || []);
      } catch (err) {
        console.error('Failed to load dashboard:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [id]);

  // ── Filtered games ────────────────────────
  const filteredGames = useMemo(() => {
    if (teamFilter === 'all') return games;
    return games.filter((g) => g.teamId === teamFilter);
  }, [games, teamFilter]);

  // ── Aggregated stats ──────────────────────
  const battingStats = useMemo(
    () => aggregateBatting(filteredGames),
    [filteredGames]
  );
  const pitchingStats = useMemo(
    () => aggregatePitching(filteredGames),
    [filteredGames]
  );
  const fieldingStats = useMemo(
    () => aggregateFielding(filteredGames),
    [filteredGames]
  );

  const battingGameData = useMemo(() => {
    return filteredGames.filter((g) => g.batting && g.batting.ab > 0);
  }, [filteredGames]);

  const pitchingGameData = useMemo(() => {
    return filteredGames.filter((g) => g.pitching);
  }, [filteredGames]);

  // ── Insights ──────────────────────────────
  const insights = useMemo(
    () => generateInsights(filteredGames, battingStats, pitchingStats),
    [filteredGames, battingStats, pitchingStats]
  );

  const benchmarkTier = useMemo(
    () => getBenchmarkTier(player?.playingLevel),
    [player]
  );

  const battingBenchmarks = useMemo(() => {
    if (!isProOrAbove || !benchmarkTier || !battingStats) return null;
    const b = benchmarkTier.batting;
    const hasEnoughAB = (battingStats.ab || 0) >= 10;
    return {
      avg: hasEnoughAB ? getBenchmarkRating(battingStats.avg, b.avg) : null,
      obp: hasEnoughAB ? getBenchmarkRating(battingStats.obp, b.obp) : null,
      kPct: hasEnoughAB ? getBenchmarkRating(
        battingStats.kPct,
        { elite: b.kPct.elite, good: b.kPct.good,
          solid: b.kPct.solid, poor: b.kPct.poor },
        true
      ) : null,
      bbPct: hasEnoughAB ? getBenchmarkRating(battingStats.bbPct, b.bbPct) : null,
    };
  }, [benchmarkTier, battingStats, isProOrAbove]);

  const pitchingBenchmarks = useMemo(() => {
    if (!isProOrAbove || !benchmarkTier || !pitchingStats) return null;
    const p = benchmarkTier.pitching;
    const hasEnoughIP = (pitchingStats.ipDecimal || 0) >= 3;
    return {
      era: hasEnoughIP ? getBenchmarkRating(pitchingStats.era, p.era, true) : null,
      whip: hasEnoughIP ? getBenchmarkRating(pitchingStats.whip, p.whip, true) : null,
      k9: hasEnoughIP ? getBenchmarkRating(pitchingStats.k9, p.k9) : null,
    };
  }, [benchmarkTier, pitchingStats, isProOrAbove]);

  const milestones = useMemo(
    () =>
      detectMilestones(filteredGames, battingStats, pitchingStats, player).filter(
        (m) => !dismissedKeys.has(`${m.type}_${m.gameId}`)
      ),
    [filteredGames, battingStats, pitchingStats, player, dismissedKeys]
  );

  // ── Recent games ──────────────────────────
  const recentGames = useMemo(
    () =>
      [...filteredGames]
        .sort((a, b) => new Date(b.gameDate) - new Date(a.gameDate))
        .slice(0, 5),
    [filteredGames]
  );

  // ── Team tabs (only teams with games) ─────
  const teamTabs = useMemo(() => {
    if (!player || !player.teams) return [];
    return player.teams.filter((t) => games.some((g) => g.teamId === t.id));
  }, [player, games]);

  // ─────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────

  if (loading) {
    return (
      <div className="dash-page">
        <div className="analytics-page-wrap">
          <div className="analytics-empty">Loading…</div>
        </div>
      </div>
    );
  }

  if (!player) {
    return (
      <div className="dash-page">
        <div className="analytics-page-wrap">
          <div className="analytics-empty">Player not found.</div>
        </div>
      </div>
    );
  }

  const currentTeam =
    player.teams && player.teams.length > 0
      ? player.teams.find((t) => t.id === player.currentTeamId) ||
        player.teams[0]
      : null;

  const needMoreGames = filteredGames.length < 3;
  const showTrends = filteredGames.length >= 5;

  return (
    <div className="dash-page">
      <div className="analytics-page-wrap">
        {/* ── Header ──────────────────────── */}
        <div className="analytics-page-header">
          <button
            className="analytics-back-btn"
            onClick={() => navigate('/analytics/players')}
          >
            ← Players
          </button>
          <h1 className="analytics-page-title">
            {player.firstName} {player.lastName}
          </h1>
          <button
            className="analytics-add-btn"
            onClick={() => navigate('/analytics/tracker', { state: { preselectedPlayerId: player?.id } })}
          >
            + Game
          </button>
        </div>

        {/* ── Hero banner ─────────────────── */}
        {(() => {
          const banner = getHeroBanner(
            player,
            battingStats,
            pitchingStats,
            battingBenchmarks,
            pitchingBenchmarks
          );
          if (!banner) return null;
          return (
            <div className="dash-hero-banner">
              <div className="dash-hero-banner-label">
                Season Highlight
              </div>
              <div className="dash-hero-banner-headline">
                {banner.headline}
              </div>
              <div className="dash-hero-banner-sub">
                {banner.sub}
              </div>
            </div>
          );
        })()}

        {/* ── Player meta ─────────────────── */}
        <div className="dash-player-meta">
          <span className="player-sport-badge">{player.sport}</span>
          {player.gradYear && (
            <span className="dash-meta-item">Class of {player.gradYear}</span>
          )}
          {currentTeam && (
            <span className="dash-meta-item dash-current-team">
              {currentTeam.name}
            </span>
          )}
          {player.positions && player.positions.length > 0 && (
            <span className="dash-meta-item">
              {player.positions.join(' · ')}
            </span>
          )}
          <button
            className="dash-edit-btn"
            onClick={() => navigate(`/analytics/players/${id}/edit`)}
          >
            Edit
          </button>
        </div>

        {/* ── Brag card quick actions ──────── */}
        <div className="dash-brag-actions">
          <button
            className="dash-brag-btn"
            onClick={() => navigate(`/analytics/players/${id}/brag-cards`)}
          >
            Brag Cards →
          </button>
          <button
            className="dash-brag-btn"
            onClick={() => {
              const playerFullName = `${player.firstName} ${player.lastName}`;
              navigate(`/brag-card?playerId=${id}&highlightPlayer=${encodeURIComponent(playerFullName)}`);
            }}
          >
            + Create Brag Card
          </button>
        </div>

        {/* ── Team filter tabs ─────────────── */}
        {teamTabs.length > 0 && (
          <div className="dash-team-tabs">
            <button
              className={`dash-team-tab ${teamFilter === 'all' ? 'active' : ''}`}
              onClick={() => setTeamFilter('all')}
            >
              All Teams
              <span className="dash-tab-count">{games.length}</span>
            </button>
            {teamTabs.map((team) => (
              <button
                key={team.id}
                className={`dash-team-tab ${teamFilter === team.id ? 'active' : ''}`}
                onClick={() => setTeamFilter(team.id)}
              >
                {team.name}
                <span className="dash-tab-count">
                  {games.filter((g) => g.teamId === team.id).length}
                </span>
              </button>
            ))}
          </div>
        )}

        {/* ── No games state ───────────────── */}
        {filteredGames.length === 0 ? (
          <div className="analytics-empty">
            <div className="analytics-empty-icon">📊</div>
            <div className="analytics-empty-text">No games recorded yet.</div>
            <button
              className="analytics-empty-btn"
              onClick={() => navigate('/analytics/tracker', { state: { preselectedPlayerId: player?.id } })}
            >
              Track First Game
            </button>
          </div>
        ) : (
          <>
            {/* ── Needs more games notice ───── */}

            {/* ── BRAG CARD BANNER ─────────── */}
            {milestones.length > 0 && (() => {
              const m = milestones[0];
              const playerName = `${player.firstName} ${player.lastName}`;
              const params = new URLSearchParams({
                milestone: m.type,
                playerId: id,
                highlightPlayer: playerName,
                highlightStats: m.statLine,
              });
              return (
                <div className="brag-banner">
                  <div className="brag-banner-left">
                    <span className="brag-banner-emoji">{m.emoji}</span>
                    <div className="brag-banner-text">
                      <div className="brag-banner-label">
                        {player.firstName} hit a milestone — {m.label}
                      </div>
                      <div className="brag-banner-stat">{m.statLine}</div>
                    </div>
                  </div>
                  <button
                    className="brag-banner-dismiss"
                    aria-label="Dismiss"
                    onClick={() => {
                      const key = `${m.type}_${m.gameId}`;
                      setDismissedKeys((prev) => {
                        const next = new Set(prev);
                        next.add(key);
                        try {
                          localStorage.setItem(
                            `playmaker_milestones_${id}`,
                            JSON.stringify([...next])
                          );
                        } catch {}
                        return next;
                      });
                    }}
                  >
                    ×
                  </button>
                  <button
                    className="brag-banner-btn"
                    onClick={() => navigate(`/brag-card?${params.toString()}`)}
                  >
                    Create Brag Card →
                  </button>
                </div>
              );
            })()}

            {/* ── TAB BAR ─────────────────── */}
            <div className="dash-tab-bar">
              {[
                { id: 'overview', label: 'Overview' },
                { id: 'batting', label: 'Batting', hidden: !battingStats },
                { id: 'pitching', label: 'Pitching', hidden: !pitchingStats },
                { id: 'fielding', label: 'Fielding', hidden: !fieldingStats?.positions?.length },
                { id: 'insights', label: 'Insights' },
              ].filter(t => !t.hidden).map(tab => (
                <button
                  key={tab.id}
                  className={`dash-tab-btn${activeTab === tab.id ? ' active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* ── BATTING ─────────────────── */}
            {activeTab === 'batting' && battingStats && (
              <div className="dash-section">
                <div className="dash-section-header">
                  <span>Batting</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span className="dash-section-games">{battingStats.gameCount} games</span>
                    <button
                      className="dash-coach-btn"
                      onClick={() => setCoachModal({
                        open: true,
                        statGroup: 'Batting',
                        initialMessage: null,
                      })}
                    >
                      💡 Ask the Analyst
                    </button>
                  </div>
                </div>

                {/* Trends toggle */}
                {showTrendsBatting && battingGameData.length >= 3 && (
                  <div className="dash-charts-row">
                    <TrendChart
                      games={battingGameData}
                      statFn={(g) => g.batting.ab > 0 ? g.batting.h / g.batting.ab : null}
                      label="AVG" color="var(--amber, #e8c84a)"
                      format={(v) => v.toFixed(3).replace(/^0/, '')}
                    />
                    <TrendChart
                      games={battingGameData}
                      statFn={(g) => {
                        const den = (g.batting.ab||0)+(g.batting.bb||0)+(g.batting.hbp||0)+(g.batting.sf||0);
                        return den > 0 ? ((g.batting.h||0)+(g.batting.bb||0)+(g.batting.hbp||0))/den : null;
                      }}
                      label="OBP" color="#68d391"
                      format={(v) => v.toFixed(3).replace(/^0/, '')}
                    />
                    <TrendChart
                      games={battingGameData}
                      statFn={(g) => g.batting.pa > 0 ? (g.batting.k/g.batting.pa)*100 : null}
                      label="K/PA" color="#fc8181" inverse
                      format={(v) => v.toFixed(0)+'%'}
                    />
                  </div>
                )}
                <button
                  className="dash-trends-toggle"
                  onClick={() => setShowTrendsBatting(v => !v)}
                >
                  {showTrendsBatting ? '▲ Hide Trends' : '▼ Show Trends'}
                </button>

                {/* Benchmark notice */}
                {!isProOrAbove && (
                  <div className="dash-notice">
                    🔒 Benchmark comparisons are available on Pro. <a href="/membership">Upgrade →</a>
                  </div>
                )}
                {isProOrAbove && (battingStats.ab || 0) < 10 && (
                  <div className="dash-notice">
                    🔒 {10 - (battingStats.ab || 0)} more AB needed to unlock benchmark comparisons for {player?.playingLevel || 'this level'}.
                  </div>
                )}

                {/* ── Contact ── */}
                <div className="dash-group-header">Contact</div>
                <div className="dash-stat-grid">
                  <div className="dash-stat-cell">
                    <div className="dash-stat-value" style={battingBenchmarks?.avg ? { color: battingBenchmarks.avg.color } : undefined}>{formatAvg(battingStats.avg)}</div>
                    <div className="dash-stat-label">AVG</div>
                    {battingBenchmarks?.avg && <BenchmarkBadge rating={battingBenchmarks.avg} levelLabel={player?.playingLevel} />}
                  </div>
                  <div className="dash-stat-cell">
                    <div className="dash-stat-value">{formatAvg(battingStats.babip)}</div>
                    <div className="dash-stat-label">BABIP</div>
                  </div>
                  <div className="dash-stat-cell">
                    <div className="dash-stat-value">{battingStats.contactPct != null ? battingStats.contactPct.toFixed(1)+'%' : '—'}</div>
                    <div className="dash-stat-label">Contact%</div>
                  </div>
                  <StatCell label="H" value={battingStats.h} />
                  <StatCell label="1B" value={battingStats['1b']} />
                  <StatCell label="2B" value={battingStats['2b']} />
                  <StatCell label="3B" value={battingStats['3b']} />
                  <StatCell label="AB" value={battingStats.ab} />
                  <StatCell label="PA" value={battingStats.pa} />
                </div>

                {/* ── Power ── */}
                <div className="dash-group-header">Power</div>
                <div className="dash-stat-grid">
                  <div className="dash-stat-cell">
                    <div className="dash-stat-value">{formatAvg(battingStats.slg)}</div>
                    <div className="dash-stat-label">SLG</div>
                  </div>
                  <StatCell label="HR" value={battingStats.hr} />
                  <StatCell label="TB" value={battingStats.tb} />
                  <StatCell label="R" value={battingStats.r} />
                </div>

                {/* ── Plate Discipline ── */}
                <div className="dash-group-header">Plate Discipline</div>
                <div className="dash-stat-grid">
                  <div className="dash-stat-cell">
                    <div className="dash-stat-value" style={battingBenchmarks?.obp ? { color: battingBenchmarks.obp.color } : undefined}>{formatAvg(battingStats.obp)}</div>
                    <div className="dash-stat-label">OBP</div>
                    {battingBenchmarks?.obp && <BenchmarkBadge rating={battingBenchmarks.obp} levelLabel={player?.playingLevel} />}
                  </div>
                  <div className="dash-stat-cell">
                    <div className="dash-stat-value">{formatAvg(battingStats.ops)}</div>
                    <div className="dash-stat-label">OPS</div>
                  </div>
                  <div className="dash-stat-cell">
                    <div className="dash-stat-value" style={battingBenchmarks?.kPct ? { color: battingBenchmarks.kPct.color } : undefined}>{battingStats.kPct != null ? battingStats.kPct.toFixed(1)+'%' : '—'}</div>
                    <div className="dash-stat-label">K/PA</div>
                    {battingBenchmarks?.kPct && <BenchmarkBadge rating={battingBenchmarks.kPct} levelLabel={player?.playingLevel} />}
                  </div>
                  <div className="dash-stat-cell">
                    <div className="dash-stat-value" style={battingBenchmarks?.bbPct ? { color: battingBenchmarks.bbPct.color } : undefined}>{battingStats.bbPct != null ? battingStats.bbPct.toFixed(1)+'%' : '—'}</div>
                    <div className="dash-stat-label">BB%</div>
                    {battingBenchmarks?.bbPct && <BenchmarkBadge rating={battingBenchmarks.bbPct} levelLabel={player?.playingLevel} />}
                  </div>
                  <StatCell label="BB" value={battingStats.bb} />
                  <StatCell label="K" value={battingStats.k} />
                  <StatCell label="HBP" value={battingStats.hbp} />
                </div>

                {/* ── Situational ── */}
                <div className="dash-group-header">Situational</div>
                <div className="dash-stat-grid">
                  {battingStats.ab_risp > 0 && (
                    <div className="dash-stat-cell">
                      <div className="dash-stat-value">{formatAvg(battingStats.ab_risp > 0 ? battingStats.h_risp / battingStats.ab_risp : null)}</div>
                      <div className="dash-stat-label">RISP AVG</div>
                    </div>
                  )}
                  {battingStats.twoOutAB > 0 && (
                    <div className="dash-stat-cell">
                      <div className="dash-stat-value">{formatAvg(battingStats.twoOutAvg)}</div>
                      <div className="dash-stat-label">2-Out AVG</div>
                    </div>
                  )}
                  {battingStats.twoOutRBI > 0 && <StatCell label="2-Out RBI" value={battingStats.twoOutRBI} />}
                  {battingStats.twoOutRISP_AB > 0 && (
                    <div className="dash-stat-cell">
                      <div className="dash-stat-value">{formatAvg(battingStats.twoOutRISPAvg)}</div>
                      <div className="dash-stat-label">2-Out RISP</div>
                    </div>
                  )}
                  <StatCell label="RBI" value={battingStats.rbi} />
                  {battingStats.rbi_risp > 0 && <StatCell label="RBI/RISP" value={battingStats.rbi_risp} />}
                  <StatCell label="SB" value={battingStats.sb} />
                  <StatCell label="CS" value={battingStats.cs} />
                  <StatCell label="SF" value={battingStats.sf} />
                  <StatCell label="SH" value={battingStats.sh} />
                  <StatCell label="ROE" value={battingStats.roe} />
                  <StatCell label="FC" value={battingStats.fc} />
                  <StatCell label="GIDP" value={battingStats.gidp} />
                  {battingStats.ibb > 0 && <StatCell label="IBB" value={battingStats.ibb} />}
                </div>
              </div>
            )}

            {/* ── PITCHING ────────────────── */}
            {activeTab === 'pitching' && pitchingStats && (
              <div className="dash-section">
                <div className="dash-section-header">
                  <span>Pitching</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span className="dash-section-games">{pitchingStats.gameCount} games</span>
                    <button
                      className="dash-coach-btn"
                      onClick={() => setCoachModal({
                        open: true,
                        statGroup: 'Pitching',
                        initialMessage: null,
                      })}
                    >
                      💡 Ask the Analyst
                    </button>
                  </div>
                </div>

                {/* Trends toggle */}
                {showTrendsPitching && pitchingGameData.length >= 3 && (
                  <div className="dash-charts-row">
                    <TrendChart
                      games={pitchingGameData}
                      statFn={(g) => {
                        const ipVal = parseFloat(g.pitching.ip || 0);
                        const full = Math.floor(ipVal);
                        const outs = Math.round((ipVal - full) * 10);
                        const ipDec = full + outs / 3;
                        return ipDec > 0 ? ((g.pitching.er || 0) / ipDec) * 9 : null;
                      }}
                      label="ERA" color="#fc8181" inverse
                      format={(v) => v.toFixed(2)}
                    />
                    <TrendChart
                      games={pitchingGameData}
                      statFn={(g) => {
                        const ipVal = parseFloat(g.pitching.ip || 0);
                        const full = Math.floor(ipVal);
                        const outs = Math.round((ipVal - full) * 10);
                        const ipDec = full + outs / 3;
                        return ipDec > 0 ? ((g.pitching.bb||0)+(g.pitching.h||0))/ipDec : null;
                      }}
                      label="WHIP" color="#f6ad55" inverse
                      format={(v) => v.toFixed(2)}
                    />
                    <TrendChart
                      games={pitchingGameData}
                      statFn={(g) => {
                        const ipVal = parseFloat(g.pitching.ip || 0);
                        const full = Math.floor(ipVal);
                        const outs = Math.round((ipVal - full) * 10);
                        const ipDec = full + outs / 3;
                        return ipDec > 0 ? ((g.pitching.k||0)/ipDec)*9 : null;
                      }}
                      label="K/9" color="#68d391"
                      format={(v) => v.toFixed(1)}
                    />
                  </div>
                )}
                <button
                  className="dash-trends-toggle"
                  onClick={() => setShowTrendsPitching(v => !v)}
                >
                  {showTrendsPitching ? '▲ Hide Trends' : '▼ Show Trends'}
                </button>

                {/* Benchmark notice */}
                {!isProOrAbove && (
                  <div className="dash-notice">
                    🔒 Benchmark comparisons are available on Pro. <a href="/membership">Upgrade →</a>
                  </div>
                )}
                {isProOrAbove && (pitchingStats.ipDecimal || 0) < 3 && (
                  <div className="dash-notice">
                    🔒 {(3 - (pitchingStats.ipDecimal || 0)).toFixed(1)} more IP needed to unlock benchmark comparisons for {player?.playingLevel || 'this level'}.
                  </div>
                )}

                {/* ── Results ── */}
                <div className="dash-group-header">Results</div>
                {/* Record row: W / L / S / IP */}
                <div className="dash-stat-grid" style={{ marginBottom: 8 }}>
                  <StatCell label="W" value={pitchingStats.wins} />
                  <StatCell label="L" value={pitchingStats.losses} />
                  <StatCell label="S" value={pitchingStats.saves} />
                  <StatCell label="IP" value={pitchingStats.ip} />
                  <StatCell label="HLD" value={pitchingStats.holds} />
                  <StatCell label="BS" value={pitchingStats.blownSaves} />
                </div>
                {/* Rate stats row: ERA / FIP / WHIP */}
                <div className="dash-stat-grid" style={{ marginBottom: 8 }}>
                  <div className="dash-stat-cell">
                    <div className="dash-stat-value" style={pitchingBenchmarks?.era ? { color: pitchingBenchmarks.era.color } : undefined}>{formatEra(pitchingStats.era)}</div>
                    <div className="dash-stat-label">ERA</div>
                    {pitchingBenchmarks?.era && <BenchmarkBadge rating={pitchingBenchmarks.era} levelLabel={player?.playingLevel} />}
                  </div>
                  <div className="dash-stat-cell">
                    <div className="dash-stat-value">{pitchingStats.fip != null ? pitchingStats.fip.toFixed(2) : '—'}</div>
                    <div className="dash-stat-label">FIP</div>
                  </div>
                  <div className="dash-stat-cell">
                    <div className="dash-stat-value" style={pitchingBenchmarks?.whip ? { color: pitchingBenchmarks.whip.color } : undefined}>{pitchingStats.whip != null ? pitchingStats.whip.toFixed(2) : '—'}</div>
                    <div className="dash-stat-label">WHIP</div>
                    {pitchingBenchmarks?.whip && <BenchmarkBadge rating={pitchingBenchmarks.whip} levelLabel={player?.playingLevel} />}
                  </div>
                </div>
                {/* Counting stats row: H / R / ER / HR */}
                <div className="dash-stat-grid">
                  <StatCell label="H" value={pitchingStats.h} />
                  <StatCell label="R" value={pitchingStats.r} />
                  <StatCell label="ER" value={pitchingStats.er} />
                  <StatCell label="HR" value={pitchingStats.hr} />
                </div>

                {/* ── Stuff ── */}
                <div className="dash-group-header">Stuff</div>
                <div className="dash-stat-grid">
                  <div className="dash-stat-cell">
                    <div className="dash-stat-value" style={pitchingBenchmarks?.k9 ? { color: pitchingBenchmarks.k9.color } : undefined}>{pitchingStats.k9 != null ? pitchingStats.k9.toFixed(1) : '—'}</div>
                    <div className="dash-stat-label">K/9</div>
                    {pitchingBenchmarks?.k9 && <BenchmarkBadge rating={pitchingBenchmarks.k9} levelLabel={player?.playingLevel} />}
                  </div>
                  <div className="dash-stat-cell">
                    <div className="dash-stat-value">{pitchingStats.kBb != null ? pitchingStats.kBb.toFixed(2) : '—'}</div>
                    <div className="dash-stat-label">K/BB</div>
                  </div>
                  <StatCell label="K" value={pitchingStats.k} />
                  <div className="dash-stat-cell">
                    <div className="dash-stat-value">{pitchingStats.avgVelo != null ? pitchingStats.avgVelo.toFixed(1) : '—'}</div>
                    <div className="dash-stat-label">Avg Velo</div>
                  </div>
                </div>

                {/* ── Command ── */}
                <div className="dash-group-header">Command</div>
                <div className="dash-stat-grid">
                  <div className="dash-stat-cell">
                    <div className="dash-stat-value">{pitchingStats.bb9 != null ? pitchingStats.bb9.toFixed(1) : '—'}</div>
                    <div className="dash-stat-label">BB/9</div>
                  </div>
                  <div className="dash-stat-cell">
                    <div className="dash-stat-value">{pitchingStats.fpsPct != null ? pitchingStats.fpsPct.toFixed(0)+'%' : '—'}</div>
                    <div className="dash-stat-label">FPS%</div>
                  </div>
                  <div className="dash-stat-cell">
                    <div className="dash-stat-value">{pitchingStats.strikePct != null ? pitchingStats.strikePct+'%' : '—'}</div>
                    <div className="dash-stat-label">Strike%</div>
                  </div>
                  <StatCell label="BB" value={pitchingStats.bb} />
                  <StatCell label="HBP" value={pitchingStats.hbp} />
                  <StatCell label="WP" value={pitchingStats.wp} />
                  <StatCell label="BK" value={pitchingStats.balks} />
                </div>

                {/* ── Usage ── */}
                <div className="dash-group-header">Usage</div>
                <div className="dash-stat-grid">
                  <StatCell label="IP" value={pitchingStats.ip} />
                  <StatCell label="BF" value={pitchingStats.bf} />
                  <StatCell label="Pitches" value={pitchingStats.pitches} />
                  <StatCell label="Games" value={pitchingStats.gameCount} />
                </div>

                {/* ── Baserunning Against ── */}
                <div className="dash-group-header">Baserunning Against</div>
                <div className="dash-stat-grid">
                  <StatCell label="SBA" value={pitchingStats.csAttempts} />
                  <StatCell label="SB" value={pitchingStats.sbAllowed} />
                  <StatCell label="CS" value={pitchingStats.csOuts} />
                  <StatCell label="PIK" value={pitchingStats.pickoffs} />
                  {pitchingStats.csAttempts > 0 && (
                    <div className="dash-stat-cell">
                      <div className="dash-stat-value">{Math.round((pitchingStats.csOuts / pitchingStats.csAttempts) * 100)}%</div>
                      <div className="dash-stat-label">CS%</div>
                    </div>
                  )}
                </div>
                {/* ── Pitch Arsenal ── */}
                <div className="dash-group-header">Pitch Arsenal</div>
                {pitchingStats.pitchTypeStats && pitchingStats.pitchTypeStats.length > 0 ? (
                  <div className="dash-arsenal-table">
                    <div className="dash-arsenal-header">
                      <span>Pitch</span>
                      <span>Usage</span>
                      <span>Strike%</span>
                      <span>Whiff%</span>
                      <span>In Play%</span>
                    </div>
                    {pitchingStats.pitchTypeStats.map((pt) => (
                      <div key={pt.type} className="dash-arsenal-row">
                        <span className="dash-arsenal-pitch">{pt.type}</span>
                        <span>{pt.usagePct}%</span>
                        <span>{pt.strikePct}%</span>
                        <span>{pt.swingingPct}%</span>
                        <span>{pt.ballInPlayPct}%</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="dash-notice">
                    No pitch types tracked yet. Select a pitch type when tracking to unlock arsenal analytics.
                  </div>
                )}
              </div>
            )}

            {/* ── FIELDING ────────────────── */}
            {activeTab === 'fielding' && fieldingStats && (
              <div className="dash-section">
                <div className="dash-section-header">
                  <span>Fielding</span>
                  <button
                    className="dash-coach-btn"
                    onClick={() => setCoachModal({
                      open: true,
                      statGroup: 'Fielding',
                      initialMessage: null,
                    })}
                  >
                    💡 AI Coach
                  </button>
                </div>
                {/* Per-position groups */}
                {fieldingStats.positions.map((pos) => (
                  <div key={pos.position}>
                    <div className="dash-group-header">{pos.position}</div>
                    <div className="dash-stat-grid">
                      <div className="dash-stat-cell">
                        <div className="dash-stat-value">
                          {pos.fpct >= 1 ? '1.000' : pos.fpct.toFixed(3).replace(/^0/, '')}
                        </div>
                        <div className="dash-stat-label">FPCT</div>
                      </div>
                      <StatCell label="PO" value={pos.po} />
                      <StatCell label="A" value={pos.assists} />
                      <div className="dash-stat-cell">
                        <div className={`dash-stat-value${pos.errors > 0 ? ' dash-error' : ''}`}>
                          {pos.errors}
                        </div>
                        <div className="dash-stat-label">E</div>
                      </div>
                      <StatCell label="Inn" value={pos.innings > 0 ? pos.innings.toFixed(1) : '—'} />
                      <StatCell label="DP" value={pos.dp} />
                      {pos.position === 'C' && (
                        <>
                          <StatCell label="K" value={pos.po - (pos.pickoffCS || 0)} />
                          <StatCell label="PB" value={pos.passedBalls} />
                          <StatCell label="Scrd/PB" value={pos.scoredOnPassedBall || 0} />
                          <StatCell label="BLK" value={pos.blockedBalls} />
                          <StatCell label="D3K" value={pos.droppedThird} />
                          <StatCell label="SBA" value={pos.sbAllowed} />
                          <StatCell label="CS" value={pos.caughtStealing} />
                          <StatCell
                            label="CS%"
                            value={
                              (pos.sbAllowed + pos.caughtStealing) > 0
                                ? Math.round((pos.caughtStealing / (pos.sbAllowed + pos.caughtStealing)) * 100) + '%'
                                : '—'
                            }
                          />
                          <StatCell label="PO-CS" value={pos.pickoffCS} />
                        </>
                      )}
                      {pos.position === '1B' && (
                        <>
                          <StatCell
                            label="RF"
                            value={pos.innings > 0 ? (9 * (pos.po + pos.assists) / pos.innings).toFixed(2) : '—'}
                          />
                          <StatCell label="Scoops" value={pos.scoops || 0} />
                        </>
                      )}
                    </div>
                  </div>
                ))}

                {/* Overall totals */}
                {fieldingStats.positions.length > 1 && (
                  <div>
                    <div className="dash-group-header">Overall</div>
                    <div className="dash-stat-grid">
                      <div className="dash-stat-cell">
                        <div className="dash-stat-value">
                          {fieldingStats.totals.fpct >= 1 ? '1.000' : fieldingStats.totals.fpct.toFixed(3).replace(/^0/, '')}
                        </div>
                        <div className="dash-stat-label">FPCT</div>
                      </div>
                      <StatCell label="PO" value={fieldingStats.totals.po} />
                      <StatCell label="A" value={fieldingStats.totals.assists} />
                      <div className="dash-stat-cell">
                        <div className={`dash-stat-value${fieldingStats.totals.errors > 0 ? ' dash-error' : ''}`}>
                          {fieldingStats.totals.errors}
                        </div>
                        <div className="dash-stat-label">E</div>
                      </div>
                      <StatCell label="DP" value={fieldingStats.totals.dp} />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── SMART INSIGHTS ──────────── */}
            {activeTab === 'insights' && !needMoreGames && (
              <div className="dash-section">
                <button
                  className="dash-insights-toggle"
                  onClick={() => setShowInsights(!showInsights)}
                >
                  <div className="dash-insights-toggle-left">
                    <span className="dash-insights-toggle-icon">🧠</span>
                    <span className="dash-insights-toggle-label">
                      Smart Insights
                    </span>
                    {insights.length > 0 && (
                      <span className="dash-insights-badge">
                        {insights.length}
                      </span>
                    )}
                  </div>
                  <span className="dash-insights-chevron">
                    {showInsights ? '▲' : '▼'}
                  </span>
                </button>

                {showInsights && (
                  <div className="dash-insights-body">
                    {insights.length === 0 ? (
                      <div className="dash-insights-empty">
                        Not enough data variation yet to generate insights. Keep
                        tracking games and patterns will emerge!
                      </div>
                    ) : (
                      <div className="dash-insights-list">
                        {insights.map((insight, i) => (
                          <InsightCard key={i} insight={insight} />
                        ))}
                      </div>
                    )}
                    {!showTrends && (
                      <div className="dash-insights-more-notice">
                        Track {5 - filteredGames.length} more game
                        {5 - filteredGames.length !== 1 ? 's' : ''} to unlock
                        trend arrows and deeper pattern analysis.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ── OVERVIEW ────────────────── */}
            {activeTab === 'overview' && (
              <>
                {/* Top stats snapshot */}
                <div className="dash-section">
                  <div className="dash-section-header">
                    <span>📈 Season At a Glance</span>
                    <span className="dash-game-count-label">
                      {filteredGames.length} game{filteredGames.length !== 1 ? 's' : ''} tracked
                    </span>
                  </div>
                  {battingStats && (
                    <>
                      {pitchingStats && (
                        <div className="dash-overview-role-label">Batting</div>
                      )}
                      <div className="dash-overview-grid">
                        <div className="dash-overview-cell">
                          <div className="dash-overview-value">{formatAvg(battingStats.avg)}</div>
                          <div className="dash-overview-label">AVG</div>
                          {battingBenchmarks?.avg && <BenchmarkBadge rating={battingBenchmarks.avg} levelLabel={player?.playingLevel} />}
                        </div>
                        <div className="dash-overview-cell">
                          <div className="dash-overview-value">{formatAvg(battingStats.obp)}</div>
                          <div className="dash-overview-label">OBP</div>
                          {battingBenchmarks?.obp && <BenchmarkBadge rating={battingBenchmarks.obp} levelLabel={player?.playingLevel} />}
                        </div>
                        <div className="dash-overview-cell">
                          <div className="dash-overview-value">{battingStats.hr ?? '—'}</div>
                          <div className="dash-overview-label">HR</div>
                        </div>
                        <div className="dash-overview-cell">
                          <div className="dash-overview-value">{battingStats.rbi ?? '—'}</div>
                          <div className="dash-overview-label">RBI</div>
                        </div>
                      </div>
                    </>
                  )}
                  {pitchingStats && (
                    <>
                      {battingStats && (
                        <div className="dash-overview-role-label">Pitching</div>
                      )}
                      <div className="dash-overview-grid">
                        <div className="dash-overview-cell">
                          <div className="dash-overview-value">{formatEra(pitchingStats.era)}</div>
                          <div className="dash-overview-label">ERA</div>
                          {pitchingBenchmarks?.era && <BenchmarkBadge rating={pitchingBenchmarks.era} levelLabel={player?.playingLevel} />}
                        </div>
                        <div className="dash-overview-cell">
                          <div className="dash-overview-value">{pitchingStats.whip != null ? pitchingStats.whip.toFixed(2) : '—'}</div>
                          <div className="dash-overview-label">WHIP</div>
                          {pitchingBenchmarks?.whip && <BenchmarkBadge rating={pitchingBenchmarks.whip} levelLabel={player?.playingLevel} />}
                        </div>
                        <div className="dash-overview-cell">
                          <div className="dash-overview-value">{pitchingStats.k ?? '—'}</div>
                          <div className="dash-overview-label">K</div>
                        </div>
                        <div className="dash-overview-cell">
                          <div className="dash-overview-value">{pitchingStats.ip ?? '—'}</div>
                          <div className="dash-overview-label">IP</div>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Insights preview — top 2 only */}
                {needMoreGames && (
                  <div className="dash-section">
                    <div className="dash-section-header">
                      <span>Insights</span>
                    </div>
                    <div className="dash-insights-teaser">
                      <div className="dash-insights-teaser-icon">🔒</div>
                      <div className="dash-insights-teaser-text">
                        <strong>Track {3 - filteredGames.length} more game{3 - filteredGames.length !== 1 ? 's' : ''} to unlock insights</strong>
                        <p>Trend analysis, hot/cold streaks, plate discipline patterns, and personalized drill recommendations will appear here.</p>
                      </div>
                    </div>
                  </div>
                )}
                {!needMoreGames && insights.length > 0 && (
                  <div className="dash-section">
                    <div className="dash-section-header">
                      <span>Top Insights</span>
                      <button className="dash-view-all-btn" onClick={() => setActiveTab('insights')}>
                        See All →
                      </button>
                    </div>
                    <div className="dash-insights-list">
                      {(() => {
                        const battingInsight = insights.find((i) => i.category === 'batting');
                        const pitchingInsight = insights.find((i) => i.category === 'pitching');
                        const preview = [battingInsight, pitchingInsight].filter(Boolean);
                        if (preview.length < 2) {
                          insights.forEach((i) => {
                            if (preview.length < 2 && !preview.includes(i)) preview.push(i);
                          });
                        }
                        return preview.slice(0, 2).map((insight, i) => (
                          <InsightCard key={i} insight={insight} />
                        ));
                      })()}
                    </div>
                  </div>
                )}
                {/* AI Coach CTA */}
                {(battingStats || pitchingStats) && (
                  <div className="dash-section">
                    <div className="dash-coach-cta">
                      <div className="dash-coach-cta-left">
                        <span className="dash-coach-cta-icon">💡</span>
                        <div>
                          <div className="dash-coach-cta-title">Ask the Analyst</div>
                          <div className="dash-coach-cta-sub">Get personalized drills and development advice</div>
                        </div>
                      </div>
                      <button
                        className="dash-coach-cta-btn"
                        onClick={() => setCoachModal({
                          open: true,
                          statGroup: battingStats ? 'Batting' : 'Pitching',
                          initialMessage: battingStats
                            ? null
                            : null,
                        })}
                      >
                        Ask the Analyst →
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* ── RECENT GAMES ────────────── */}
            {activeTab === 'overview' && <div className="dash-section">
              <div className="dash-section-header">
                <span>📋 Recent Games</span>
                <button
                  className="dash-view-all-btn"
                  onClick={() => navigate(`/analytics/players/${id}/games`)}
                >
                  View All →
                </button>
              </div>
              <div className="dash-recent-list">
                {recentGames.map((game) => (
                  <div
                    key={game.id}
                    className="dash-recent-game"
                    onClick={() =>
                      game.status === 'in_progress'
                        ? setResumeModalGame(game)
                        : navigate(`/analytics/players/${id}/games/${game.id}`)
                    }
                  >
                    <div className="dash-recent-top">
                      <span className="dash-recent-date">
                        {formatDate(game.gameDate)}
                      </span>
                      <span className="dash-recent-opp">
                        vs {game.opponent || '—'}
                      </span>
                      {game.teamName && (
                        <span className="dash-recent-team">
                          {game.teamName}
                        </span>
                      )}
                      <span className={`dash-game-status ${
                        game.status !== 'in_progress'
                          ? 'completed'
                          : (game.batting || game.pitching || game.fielding)
                            ? 'in-progress'
                            : 'no-stats'
                      }`}>
                        {game.status !== 'in_progress'
                          ? 'Completed'
                          : (game.batting || game.pitching || game.fielding)
                            ? 'In Progress (Unsaved stats)'
                            : 'No Stats Entered'}
                      </span>
                    </div>
                    <div className="dash-recent-stats">
                      {game.batting && (
                        <span className="dash-recent-line batting">
                          {game.batting.h}/{game.batting.ab}
                          {game.batting.hr > 0 && ` · ${game.batting.hr} HR`}
                          {game.batting.rbi > 0 && ` · ${game.batting.rbi} RBI`}
                          {game.batting.bb > 0 && ` · ${game.batting.bb} BB`}
                          {game.batting.sb > 0 && ` · ${game.batting.sb} SB`}
                        </span>
                      )}
                      {game.pitching && (
                        <span className="dash-recent-line pitching">
                          {game.pitching.ip} IP · {game.pitching.k} K ·{' '}
                          {game.pitching.er} ER
                          {game.pitching.decision &&
                            ` · ${game.pitching.decision}`}
                        </span>
                      )}
                      {!game.batting && !game.pitching && game.fielding && Array.isArray(game.fielding) && game.fielding.length > 0 && (
                        <span className="dash-recent-line fielding">
                          {game.fielding.map(f => f.position).join(', ')}
                          {' · '}
                          {game.fielding.reduce((acc, f) => acc + (f.po || 0), 0)} PO
                          {' · '}
                          {game.fielding.reduce((acc, f) => acc + (f.a || 0), 0)} A
                          {game.fielding.reduce((acc, f) => acc + (f.e || 0), 0) > 0 &&
                            ` · ${game.fielding.reduce((acc, f) => acc + (f.e || 0), 0)} E`}
                        </span>
                      )}
                      {!game.batting && !game.pitching && (!game.fielding || game.fielding.length === 0) && (
                        <span className="dash-recent-line" style={{ color: '#8a95a8' }}>
                          No stats recorded
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>}
          </>
        )}

        {/* ── Action buttons ───────────────── */}
        <div className="dash-actions">
          <button
            className="dash-action-btn primary"
            onClick={() => navigate('/analytics/tracker', { state: { preselectedPlayerId: player?.id } })}
          >
            Track New Game
          </button>
          <button
            className="dash-action-btn secondary"
            onClick={() => navigate(`/analytics/players/${id}/games`)}
          >
            Full Game History
          </button>
        </div>

        {/* ── Stat Glossary ─────────────── */}
        <div className="dash-glossary">
          <button
            className="dash-glossary-toggle"
            onClick={() => setShowGlossary(v => !v)}
          >
            {showGlossary ? '▲ Hide Stat Glossary' : '▼ Stat Glossary'}
          </button>
          {showGlossary && (() => {
            const BATTING_GLOSSARY = [
              { abbr: 'AB', full: 'At Bats' },
              { abbr: 'AVG', full: 'Batting Average (H/AB)' },
              { abbr: 'BABIP', full: 'Batting Avg on Balls in Play' },
              { abbr: 'BB', full: 'Walks (Base on Balls)' },
              { abbr: 'BB%', full: 'Walk Rate (BB/PA)' },
              { abbr: 'CI', full: 'Catcher Interference' },
              { abbr: 'CS', full: 'Caught Stealing' },
              { abbr: 'D3K', full: 'Dropped Third Strike — batter reaches on uncaught K' },
              { abbr: 'FC', full: "Fielder's Choice" },
              { abbr: 'GIDP', full: 'Grounded Into Double Play' },
              { abbr: 'H', full: 'Hits' },
              { abbr: 'HBP', full: 'Hit By Pitch' },
              { abbr: 'HR', full: 'Home Runs' },
              { abbr: 'IBB', full: 'Intentional Walk' },
              { abbr: 'IPHR', full: 'Inside The Park Home Run' },
              { abbr: 'ISO', full: 'Isolated Power (SLG - AVG)' },
              { abbr: 'K', full: 'Strikeouts' },
              { abbr: 'K/PA', full: 'Strikeout Rate (K/PA)' },
              { abbr: 'OBP', full: 'On Base Percentage' },
              { abbr: 'OPS', full: 'On Base + Slugging' },
              { abbr: 'PA', full: 'Plate Appearances' },
              { abbr: 'PIK', full: 'Picked Off — runner caught off base by pitcher/catcher' },
              { abbr: 'R', full: 'Runs Scored' },
              { abbr: 'RBI', full: 'Runs Batted In' },
              { abbr: 'RISP', full: 'Runners In Scoring Position' },
              { abbr: 'ROE', full: 'Reached on Error' },
              { abbr: 'SB', full: 'Stolen Bases' },
              { abbr: 'SF', full: 'Sacrifice Fly' },
              { abbr: 'SH', full: 'Sacrifice Hit (Bunt)' },
              { abbr: 'SLG', full: 'Slugging Percentage' },
              { abbr: 'TB', full: 'Total Bases' },
              { abbr: '2-Out AVG', full: 'Batting Average with 2 Outs' },
              { abbr: '2-Out RBI', full: 'RBI recorded with 2 Outs' },
              { abbr: '2-Out RISP', full: 'Batting Average with 2 Outs and Runners In Scoring Position' },
            ];
            const PITCHING_GLOSSARY = [
              { abbr: 'BB', full: 'Walks (Base on Balls)' },
              { abbr: 'BB/9', full: 'Walks per 9 Innings' },
              { abbr: 'BF', full: 'Batters Faced' },
              { abbr: 'BK', full: 'Balk — illegal pitcher motion, all runners advance one base' },
              { abbr: 'BS', full: 'Blown Save — had save opportunity, gave up lead' },
              { abbr: 'CS', full: 'Caught Stealing' },
              { abbr: 'ER', full: 'Earned Runs' },
              { abbr: 'ERA', full: 'Earned Run Average' },
              { abbr: 'FIP', full: 'Fielding Independent Pitching' },
              { abbr: 'FPS%', full: 'First Pitch Strike Percentage' },
              { abbr: 'H', full: 'Hits' },
              { abbr: 'HBP', full: 'Hit By Pitch' },
              { abbr: 'HLD', full: 'Hold — relief pitcher preserves lead without finishing game' },
              { abbr: 'HR', full: 'Home Runs' },
              { abbr: 'IBB', full: 'Intentional Walk' },
              { abbr: 'IP', full: 'Innings Pitched' },
              { abbr: 'K', full: 'Strikeouts' },
              { abbr: 'K/BB', full: 'Strikeout to Walk Ratio' },
              { abbr: 'K/9', full: 'Strikeouts per 9 Innings' },
              { abbr: 'PIK', full: 'Pickoff — pitcher throws to base to retire a runner' },
              { abbr: 'R', full: 'Runs Scored' },
              { abbr: 'SBA', full: 'Stolen Base Attempts Against (Pitcher)' },
              { abbr: 'Strike%', full: 'Overall Strike Percentage' },
              { abbr: 'W', full: 'Win' },
              { abbr: 'WHIP', full: 'Walks + Hits per Inning Pitched' },
              { abbr: 'WP', full: 'Wild Pitches' },
            ];
            const FIELDING_GLOSSARY = [
              { abbr: 'A', full: 'Assists' },
              { abbr: 'BLK', full: 'Blocked Balls (Catcher)' },
              { abbr: 'CS', full: 'Caught Stealing (Catcher)' },
              { abbr: 'DP', full: 'Double Plays' },
              { abbr: 'E', full: 'Errors' },
              { abbr: 'FPCT', full: 'Fielding Percentage' },
              { abbr: 'Inn', full: 'Innings Played' },
              { abbr: 'PB', full: 'Passed Balls (Catcher)' },
              { abbr: 'PO', full: 'Putouts' },
              { abbr: 'RF', full: 'Range Factor — estimates plays made per 9 innings (9 x (PO + A) / Innings). Higher means reaching more balls, not just the ones hit right at them.' },
              { abbr: 'SBA', full: 'Stolen Base Attempts Against (Catcher)' },
              { abbr: 'Scoops', full: 'Putout made by digging out a low or short throw at 1st base' },
              { abbr: 'TC', full: 'Total Chances (PO + A + E)' },
            ];
            const ALL_GLOSSARY = [
              { abbr: 'A', full: 'Assists' },
              { abbr: 'AB', full: 'At Bats' },
              { abbr: 'AVG', full: 'Batting Average (H/AB)' },
              { abbr: 'BABIP', full: 'Batting Avg on Balls in Play' },
              { abbr: 'BB', full: 'Walks (Base on Balls)' },
              { abbr: 'BB%', full: 'Walk Rate (BB/PA)' },
              { abbr: 'BB/9', full: 'Walks per 9 Innings' },
              { abbr: 'BF', full: 'Batters Faced' },
              { abbr: 'BK', full: 'Balk — illegal pitcher motion, all runners advance one base' },
              { abbr: 'BLK', full: 'Blocked Balls (Catcher)' },
              { abbr: 'BS', full: 'Blown Save — had save opportunity, gave up lead' },
              { abbr: 'CI', full: 'Catcher Interference' },
              { abbr: 'CS', full: 'Caught Stealing' },
              { abbr: 'D3K', full: 'Dropped Third Strike — batter reaches on uncaught K' },
              { abbr: 'DP', full: 'Double Plays' },
              { abbr: 'E', full: 'Errors' },
              { abbr: 'ER', full: 'Earned Runs' },
              { abbr: 'ERA', full: 'Earned Run Average' },
              { abbr: 'FC', full: "Fielder's Choice" },
              { abbr: 'FIP', full: 'Fielding Independent Pitching' },
              { abbr: 'FPS%', full: 'First Pitch Strike Percentage' },
              { abbr: 'FPCT', full: 'Fielding Percentage' },
              { abbr: 'GIDP', full: 'Grounded Into Double Play' },
              { abbr: 'H', full: 'Hits' },
              { abbr: 'HBP', full: 'Hit By Pitch' },
              { abbr: 'HLD', full: 'Hold — relief pitcher preserves lead without finishing game' },
              { abbr: 'HR', full: 'Home Runs' },
              { abbr: 'IBB', full: 'Intentional Walk' },
              { abbr: 'Inn', full: 'Innings Played (Fielding)' },
              { abbr: 'IP', full: 'Innings Pitched' },
              { abbr: 'IPHR', full: 'Inside The Park Home Run' },
              { abbr: 'ISO', full: 'Isolated Power (SLG - AVG)' },
              { abbr: 'K', full: 'Strikeouts' },
              { abbr: 'K/BB', full: 'Strikeout to Walk Ratio' },
              { abbr: 'K/9', full: 'Strikeouts per 9 Innings' },
              { abbr: 'K/PA', full: 'Strikeout Rate (K/PA)' },
              { abbr: 'OBP', full: 'On Base Percentage' },
              { abbr: 'OPS', full: 'On Base + Slugging' },
              { abbr: 'PA', full: 'Plate Appearances' },
              { abbr: 'PB', full: 'Passed Balls (Catcher)' },
              { abbr: 'PIK', full: 'Pickoff — pitcher/catcher throws to base to retire a runner; also logged when batter is picked off' },
              { abbr: 'PO', full: 'Putouts' },
              { abbr: 'R', full: 'Runs Scored' },
              { abbr: 'RBI', full: 'Runs Batted In' },
              { abbr: 'RF', full: 'Range Factor — estimates plays made per 9 innings (9 x (PO + A) / Innings). Higher means reaching more balls, not just the ones hit right at them.' },
              { abbr: 'RISP', full: 'Runners In Scoring Position' },
              { abbr: 'ROE', full: 'Reached on Error' },
              { abbr: 'SB', full: 'Stolen Bases' },
              { abbr: 'SBA', full: 'Stolen Base Attempts Against' },
              { abbr: 'Scoops', full: 'Putout made by digging out a low or short throw at 1st base' },
              { abbr: 'SF', full: 'Sacrifice Fly' },
              { abbr: 'SH', full: 'Sacrifice Hit (Bunt)' },
              { abbr: 'SLG', full: 'Slugging Percentage' },
              { abbr: 'TB', full: 'Total Bases' },
              { abbr: 'TC', full: 'Total Chances (PO + A + E)' },
              { abbr: 'WHIP', full: 'Walks + Hits per Inning Pitched' },
              { abbr: 'WP', full: 'Wild Pitches' },
              { abbr: '2-Out AVG', full: 'Batting Average with 2 Outs' },
              { abbr: '2-Out RBI', full: 'RBI recorded with 2 Outs' },
              { abbr: '2-Out RISP', full: 'Batting Average with 2 Outs and Runners In Scoring Position' },
            ];
            const glossaryItems =
              activeTab === 'batting' ? BATTING_GLOSSARY :
              activeTab === 'pitching' ? PITCHING_GLOSSARY :
              activeTab === 'fielding' ? FIELDING_GLOSSARY :
              ALL_GLOSSARY;
            return (
              <div className="dash-glossary-grid">
                {glossaryItems.map(({ abbr, full }) => (
                  <div key={abbr} className="dash-glossary-item">
                    <span className="dash-glossary-abbr">{abbr}</span>
                    <span className="dash-glossary-full">{full}</span>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>

        <StatCoachModal
          isOpen={coachModal.open}
          onClose={() => setCoachModal({ open: false, statGroup: null, initialMessage: null })}
          playerContext={coachModal.open ? {
            playerId: player?.id,
            playerName: player?.firstName,
            playingLevel: player?.playingLevel,
            sport: player?.sport || 'baseball',
            gradYear: player?.gradYear || null,
            throws: player?.throws || null,
            bats: player?.bats || null,
            playerNotes: player?.playerNotes || null,
            teams: player?.teams || [],
            statGroup: coachModal.statGroup,
            stats: coachModal.statGroup === 'Fielding' ? {
              positions: fieldingStats?.positions?.map(p => ({
                position: p.position,
                innings: p.innings,
                po: p.po,
                assists: p.assists,
                errors: p.errors,
                fpct: p.fpct,
                dp: p.dp,
                ...(p.position === 'C' ? {
                  blockedBalls: p.blockedBalls,
                  caughtStealing: p.caughtStealing,
                  passedBalls: p.passedBalls,
                  sbAllowed: p.sbAllowed,
                } : {}),
              })),
              totals: fieldingStats?.totals,
            } : coachModal.statGroup === 'Pitching' ? {
              era: pitchingStats?.era,
              whip: pitchingStats?.whip,
              fip: pitchingStats?.fip,
              k9: pitchingStats?.k9,
              bb9: pitchingStats?.bb9,
              kBb: pitchingStats?.kBb,
              fpsPct: pitchingStats?.fpsPct,
              avgVelo: pitchingStats?.avgVelo,
              ip: pitchingStats?.ip,
              k: pitchingStats?.k,
              bb: pitchingStats?.bb,
              gameCount: pitchingStats?.gameCount,
            } : {
              avg: battingStats?.avg,
              obp: battingStats?.obp,
              slg: battingStats?.slg,
              ops: battingStats?.ops,
              kPct: battingStats?.kPct,
              bbPct: battingStats?.bbPct,
              babip: battingStats?.babip,
              hr: battingStats?.hr,
              rbi: battingStats?.rbi,
              sb: battingStats?.sb,
              cs: battingStats?.cs,
              csAttempts: battingStats?.csAttempts,
              hbp: battingStats?.hbp,
              ab: battingStats?.ab,
              pa: battingStats?.pa,
              gameCount: battingStats?.gameCount,
            },
            benchmarks: coachModal.statGroup === 'Pitching' && pitchingBenchmarks
              ? Object.fromEntries(Object.entries(pitchingBenchmarks).map(([k, v]) => [k, v ? { label: v.label, emoji: v.emoji } : null]))
              : coachModal.statGroup === 'Batting' && battingBenchmarks
              ? Object.fromEntries(Object.entries(battingBenchmarks).map(([k, v]) => [k, v ? { label: v.label, emoji: v.emoji } : null]))
              : null,
            recentGameNotes: recentGames
              .filter(g => g.notes)
              .map(g => ({ date: g.gameDate, opponent: g.opponent, notes: g.notes })),
          } : null}
          initialMessage={coachModal.initialMessage}
        />

        {resumeModalGame && (
          <div className="modal-overlay" onClick={() => setResumeModalGame(null)}>
            <div className="modal-card" onClick={(e) => e.stopPropagation()}>
              <h3>Resume this game?</h3>
              <p>
                This game is still in progress. Continue tracking it live, or just view the stats recorded so far.
              </p>
              <div className="modal-actions">
                <button className="btn-secondary" onClick={() => setResumeModalGame(null)}>
                  Cancel
                </button>
                <button
                  className="btn-secondary"
                  onClick={() => navigate(`/analytics/players/${id}/games/${resumeModalGame.id}`)}
                >
                  View Stats Only
                </button>
                <button
                  className="save-btn"
                  onClick={() => navigate('/analytics/tracker', { state: { preselectedPlayerId: id, resumeGameId: resumeModalGame.id } })}
                >
                  Resume Game
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
